import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useFormData } from '../../hooks/useFormData';
import {
  chainValidations,
  groupValidation,
  objectToString,
  stringToObject,
} from '../../utils/form-utils';
import GradeInputColumn from './DateInputField/GradeInputColumn';
import { dateIsEmpty, parseDecimalPlacesRule } from './DateInputField/utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

/** Axis keys (no spaces) used to build field ids */
export const getColumns = () => [
  'OverallTimeToHire',
  'OverallCampaignsInScope',
  'ExternalPosts',
  'ExternalCampaignsInScope',
  'NonExternalPosts',
  'NonExternalCampaignsInScope',
];
export const getRows = () => ['delegated', 'scs', 'bpss', 'ctc', 'sc', 'dv'];
export const getKeys = () => getRows().flatMap((row) => getColumns().map((col) => `${row}${col}`));

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export function CoreTtfConsolidatedInput({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const { data } = useFormData();

  useEffect(() => {
    if (getValues(question.name) === undefined) setValue(question.name, '');
  }, [getValues, question.name, setValue]);

  const aId = useId();
  const makeId = (suffix: string) => `${anchorId ?? aId}-${suffix}`;

  const initialValues = useMemo(
    () => stringToObject(getValues(question.name)),
    [getValues, question.name],
  );
  const [values, setValues] = useState(initialValues);
  const decimalPlaces = parseDecimalPlacesRule(question.rules?.decimal);
  const allowFloat = typeof decimalPlaces === 'number';

  const { onChange, name } = register(question.name, {
    validate: (value: string) =>
      chainValidations([
        groupValidation({
          value: stringToObject(value),
          answers: data?.answers,
          question,
          sumColumns: [], // no computed totals
        }),
      ]),
  });

  const labelForRow = (row: string) => {
    switch (row) {
      case 'delegated':
        return 'Delegated Grade Recruitment AA–G6';
      case 'scs':
        return 'SCS Recruitment';
      case 'bpss':
        return 'Baseline Personnel Security Standard (BPSS)';
      case 'ctc':
        return 'Counter Terrorist Check (CTC) / Level 1B';
      case 'sc':
        return 'Security Check (SC)';
      case 'dv':
        return 'Developed Vetting (DV)';
      default:
        return row;
    }
  };

  const headerLabel = (col: string) => {
    switch (col) {
      case 'OverallTimeToHire':
        return 'Overall Time Taken';
      case 'OverallCampaignsInScope':
        return 'Number of campaigns in scope';
      case 'ExternalPosts':
        return 'Externally advertised Time to Fill';
      case 'ExternalCampaignsInScope':
        return 'Number of campaigns in scope';
      case 'NonExternalPosts':
        return 'Non-external Time to Fill';
      case 'NonExternalCampaignsInScope':
        return 'Number of campaigns in scope';
      default:
        return col;
    }
  };

  const triggerChange = (updated: typeof values) => {
    const asString = objectToString(updated);
    onChange({
      target: { name, value: dateIsEmpty(asString) ? '' : asString },
    });
  };

  const handleChange = (field: keyof typeof values) => (val: string) => {
    const updated = { ...values, [field]: val };
    setValues(updated);
    triggerChange(updated);
  };

  const dividerStyle: React.CSSProperties = {
    borderRight: '2px solid #b1b4b6',
    paddingLeft: '8px',
    paddingRight: '8px',
  };
  const cellStyle: React.CSSProperties = {
    paddingLeft: '8px',
    paddingRight: '8px',
  };

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <table className="govuk-table sticky-header-table sticky-header-table--double">
        <style>{`
          .sticky-header-table .govuk-table__head .govuk-table__header {
            position: sticky;
            background: #ffffff;
            z-index: 2;
          }
          .sticky-header-table--double .govuk-table__head tr:nth-child(1) .govuk-table__header {
            top: 0;
          }
          .sticky-header-table--double .govuk-table__head tr:nth-child(2) .govuk-table__header {
            top: 2.5rem;
          }
        `}</style>
        <thead className="govuk-table__head">
          {/* Group header row */}
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header"></th>
            <th colSpan={2} className="govuk-table__header" style={dividerStyle}>
              Overall
            </th>
            <th colSpan={2} className="govuk-table__header" style={dividerStyle}>
              Externally advertised posts
            </th>
            <th colSpan={2} className="govuk-table__header">
              Non-externally advertised posts
            </th>
          </tr>
          {/* Column labels row */}
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Recruitment Type
            </th>
            {getColumns().map((col, idx) => {
              const isDivider = idx === 1 || idx === 3;
              return (
                <th
                  key={col}
                  scope="col"
                  className="govuk-table__header"
                  style={isDivider ? dividerStyle : cellStyle}
                >
                  {headerLabel(col)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {getRows().map((row) => (
            <tr key={row} className="govuk-table__row">
              <th scope="row" className="govuk-table__header">
                {labelForRow(row)}
              </th>
              {getColumns().map((col, idx) => {
                const isDivider = idx === 1 || idx === 3;
                const field = `${row}${col}` as keyof typeof values;
                return (
                  <td
                    key={String(field)}
                    className="govuk-table__cell"
                    style={isDivider ? dividerStyle : cellStyle}
                  >
                    <GradeInputColumn
                      testId={`${String(field)}-${labelForRow(row)
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                      disabled={disabled}
                      allowFloat={allowFloat}
                      decimalPlaces={decimalPlaces}
                      anchorId={makeId(String(field))}
                      onChange={handleChange(field)}
                      value={values[field] ?? ''}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
